import _ from 'lodash';

import { NAVIGATION } from '../../config';
import { isExperimentActive } from '../../experiments/experiments';
import { redirect } from '../../utils/navigation';

const isRouteAllowed = (isAllowed: boolean, path: string) => {
  return (next, goToFn = redirect) => {
    if (isAllowed) {
      next();
      return;
    }

    goToFn(path);
  };
};

const compose = (...args) => {
  return (next) => {
    let counter = 0;
    const callNext = () => {
      args[counter](() => {
        counter += 1;
        args[counter] ? callNext() : next();
      });
    };
    callNext();
  };
};

const withOrganization = (hasOrganization: boolean) => {
  return isRouteAllowed(
    hasOrganization,
    NAVIGATION.ORGANIZATION_CREATE_FIRST_STATE,
  );
};

const withoutOrganization = (hasOrganization: boolean) => {
  return isRouteAllowed(!hasOrganization, NAVIGATION.ORGANIZATION_SELECT_STATE);
};

const withExperiment = (experimentName: string) => {
  return isRouteAllowed(
    isExperimentActive(experimentName),
    NAVIGATION.ORGANIZATION_SELECT_STATE,
  );
};

const withExperiments = (experimentNames: string[]) => {
  const experimentCheckers = _.map(experimentNames, (name) =>
    withExperiment(name),
  );
  return compose(...experimentCheckers);
};

const withoutExperiment = (experimentName: string) => {
  return isRouteAllowed(
    !isExperimentActive(experimentName),
    NAVIGATION.ORGANIZATION_SELECT_STATE,
  );
};

export const denyAccess = isRouteAllowed(
  false,
  NAVIGATION.ORGANIZATION_SELECT_STATE,
);

export const onlyForLoggedUsers = (isLoggedIn) => {
  return isRouteAllowed(isLoggedIn, NAVIGATION.LOGIN_STATE);
};

export const onlyForGuests = (isLoggedIn) => {
  return isRouteAllowed(
    !isLoggedIn,
    NAVIGATION.ORGANIZATION_CREATE_FIRST_STATE,
  );
};

export const forLoggedUsersWithOrganization = (isLoggedIn, hasOrganization) => {
  return compose(
    onlyForLoggedUsers(isLoggedIn),
    withOrganization(hasOrganization),
  );
};

export const forLoggedUsersWithoutOrganization = (
  isLoggedIn,
  hasOrganization,
) => {
  return compose(
    onlyForLoggedUsers(isLoggedIn),
    withoutOrganization(hasOrganization),
  );
};

export const onlyWithExperiment = (
  isLoggedIn: boolean,
  hasOrganization: boolean,
  experimentName: string,
) =>
  compose(
    onlyForLoggedUsers(isLoggedIn),
    withOrganization(hasOrganization),
    withExperiment(experimentName),
  );

export const onlyWithExperiments = (
  isLoggedIn: boolean,
  hasOrganization: boolean,
  experimentNames: string[],
) =>
  compose(
    onlyForLoggedUsers(isLoggedIn),
    withOrganization(hasOrganization),
    withExperiments(experimentNames),
  );

export const onlyWithoutExperiment = (
  isLoggedIn,
  hasOrganization,
  experiment,
) => {
  return compose(
    onlyForLoggedUsers(isLoggedIn),
    withOrganization(hasOrganization),
    withoutExperiment(experiment),
  );
};
